/** Helpers de paginação, tempo de leitura e âncoras de títulos do blog E-FISIO. */

export const PAGE_SIZE = 12;

export function totalPaginas(total: number, pageSize = PAGE_SIZE): number {
  return Math.max(1, Math.ceil(total / pageSize));
}

export function normalizarPagina(page: unknown, total: number, pageSize = PAGE_SIZE): number {
  const n = Number(page);
  if (!Number.isFinite(n) || n < 1) return 1;
  return Math.min(Math.trunc(n), totalPaginas(total, pageSize));
}

export function paginar<T>(itens: T[], page: number, pageSize = PAGE_SIZE): T[] {
  const inicio = (page - 1) * pageSize;
  return itens.slice(inicio, inicio + pageSize);
}

/** Palavras + tempo estimado de leitura (200 palavras/min). */
export function tempoLeitura(md: string | null | undefined) {
  const palavras = (md ?? "")
    .replace(/```[\s\S]*?```/g, " ")
    .replace(/[#>*_`|\-]+/g, " ")
    .split(/\s+/)
    .filter(Boolean).length;
  return { palavras, minutos: Math.max(1, Math.round(palavras / 200)) };
}

/** Slug estável para âncoras de títulos do markdown. */
export function slugTitulo(texto: string): string {
  return texto
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, "")
    .trim()
    .replace(/\s+/g, "-")
    .slice(0, 60);
}

export interface TocItem {
  id: string;
  texto: string;
  nivel: 2 | 3;
}

/** Índice de seções a partir dos títulos ## e ### do markdown. */
export function extrairToc(md: string | null | undefined): TocItem[] {
  const linhas = (md ?? "").split("\n");
  const itens: TocItem[] = [];
  const usados = new Set<string>();
  let dentroDeCodigo = false;

  for (const linha of linhas) {
    if (linha.trimStart().startsWith("```")) {
      dentroDeCodigo = !dentroDeCodigo;
      continue;
    }
    if (dentroDeCodigo) continue;

    const m = /^(#{2,3})\s+(.+?)\s*#*\s*$/.exec(linha);
    if (!m) continue;

    const texto = m[2].replace(/[*_`]/g, "").trim();
    if (!texto) continue;

    let id = slugTitulo(texto) || `secao-${itens.length + 1}`;
    let n = 2;
    while (usados.has(id)) id = `${slugTitulo(texto)}-${n++}`;
    usados.add(id);

    itens.push({ id, texto, nivel: m[1].length === 2 ? 2 : 3 });
  }

  return itens;
}

/**
 * Divide o markdown em duas partes num limite de "## " próximo do meio,
 * para inserir um CTA no meio do artigo sem alterar o conteúdo.
 */
export function dividirNoMeio(md: string): [string, string] {
  const linhas = md.split("\n");
  if (linhas.length < 40) return [md, ""];

  const meio = Math.floor(linhas.length / 2);
  let melhor = -1;
  linhas.forEach((linha, i) => {
    if (!/^##\s+/.test(linha)) return;
    if (i < 8 || i > linhas.length - 8) return;
    if (melhor === -1 || Math.abs(i - meio) < Math.abs(melhor - meio)) melhor = i;
  });

  if (melhor === -1) return [md, ""];
  return [linhas.slice(0, melhor).join("\n"), linhas.slice(melhor).join("\n")];
}
